import { useCallback, useEffect, useState, type FC } from "react";
import dayjs from "dayjs";
import { PlusIcon } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/ds/shadcn/components/ui/button";
import { Spinner } from "@/ds/shadcn/components/ui/spinner";
import { ErrorDisplay } from "@/lib/common/ErrorDisplay";
import { getPaymentCardProviderImage } from "@/lib/media/paymentCardProviders";
import { usePaymentCards } from "@/features/paymentCards/api/usePaymentCards";
import { StripeCardEntry } from "@/features/paymentCards/components/StripeCardEntry";
import type { PaymentCardModel } from "@/features/paymentCards/models/PaymentCardModel";
import { SelectableOption } from "@/features/subscriptions/components/SelectableOption";

export interface PaymentCardPickerProps {
	value: string | null;
	onChange: (paymentCardId: string) => void;
	disabled?: boolean;
}

export const PaymentCardPicker: FC<PaymentCardPickerProps> = props => {
	const {
		value,
		onChange,
		disabled = false
	} = props;

	const queryClient = useQueryClient();
	const paymentCards = usePaymentCards();
	const [addPaymentCardVisible, setAddPaymentCardVisible] = useState(false);

	useEffect(() => {
		const firstPaymentCard = paymentCards.data?.at(0);
		if (!firstPaymentCard || paymentCards.data?.some(x => x.paymentCardId === value)) {
			return;
		}

		onChange(firstPaymentCard.paymentCardId);
	}, [paymentCards.data, value]);

	const onPaymentCardAdded = useCallback(() => {
		setAddPaymentCardVisible(false);

		const newPaymentCard = queryClient.getQueryData<PaymentCardModel[]>(["paymentCards"])?.at(-1);
		if (newPaymentCard) {
			onChange(newPaymentCard.paymentCardId);
		}
	}, [queryClient, onChange]);

	if (paymentCards.isLoading) {
		return (
			<div className="flex justify-center p-3">
				<Spinner />
			</div>
		);
	}

	if (paymentCards.data && paymentCards.data.length === 0) {
		return (
			<StripeCardEntry embedded onComplete={onPaymentCardAdded} />
		);
	}

	return (
		<div className="flex flex-col gap-3">
			<ErrorDisplay error={paymentCards.error} />

			<div role="radiogroup" aria-label="Payment card" className="flex flex-col gap-2">
				{
					paymentCards.data?.map(x => (
						<SelectableOption
							key={x.paymentCardId}
							media={<img src={getPaymentCardProviderImage(x.provider)} className="size-8 shrink-0" />}
							title={`Card ending in ${x.last4}`}
							subtitle={`Expires ${x.expiry.valueOf() === 0 ? "●●/●●" : dayjs(x.expiry).format("MM/YY")}`}
							selected={x.paymentCardId === value}
							disabled={disabled}
							onSelect={() => onChange(x.paymentCardId)} />
					))
				}
			</div>

			{
				addPaymentCardVisible ? (
					<>
						<StripeCardEntry embedded onComplete={onPaymentCardAdded} />
						<Button variant="ghost" onClick={() => setAddPaymentCardVisible(false)}>
							Cancel
						</Button>
					</>
				) : (
					<Button variant="outline" disabled={disabled} onClick={() => setAddPaymentCardVisible(true)}>
						<PlusIcon /> Add Payment Card
					</Button>
				)
			}
		</div>
	);
};
